import type { TicketMetricsResponse } from '@chatbot/shared-types';
import { EmptyState } from '../EmptyState.tsx';
import { Card, CardBody, CardHeader } from '../ui/Card.js';

export interface TicketMetricsSectionProps {
  tickets: TicketMetricsResponse | null | undefined;
}

/** Un número con su etiqueta. Sin dato no se dibuja: cero sería una afirmación. */
function Dato({ label, valor, hint }: { label: string; valor: string; hint?: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-caption text-content-tertiary">{label}</span>
      <span className="text-h4 tabular-nums text-content-primary">{valor}</span>
      {hint ? <span className="text-caption text-content-tertiary">{hint}</span> : null}
    </div>
  );
}

/** Segundos a algo que una persona lee sin convertir mentalmente. */
function duracion(segundos: number): string {
  if (segundos < 60) return `${Math.round(segundos)} s`;
  if (segundos < 3600) return `${Math.round(segundos / 60)} min`;
  const horas = segundos / 3600;
  if (horas < 48) return `${horas.toFixed(1)} h`;
  return `${Math.round(horas / 24)} días`;
}

/**
 * Métricas de soporte.
 *
 * **Nada se dibuja sin datos.** Ni la tarjeta, ni la sección. El endpoint manda
 * `null` cuando no hay con qué, y un cero se leería como "no pasó nada" o —peor,
 * en la primera respuesta— como "instantáneo". `null` es "no sabemos", y son
 * cosas distintas.
 *
 * La única excepción es el backlog: **cero abiertos es un dato bueno** y el
 * cliente tiene que verlo.
 */
export function TicketMetricsSection({ tickets }: TicketMetricsSectionProps) {
  if (!tickets) return null;

  const { backlog, resueltoPorBot, primeraRespuestaHumana, tiempoResolucion, porAgente } = tickets;

  const hayAlgo =
    backlog.abiertos > 0 ||
    backlog.cerrados > 0 ||
    resueltoPorBot !== null ||
    primeraRespuestaHumana !== null ||
    tiempoResolucion !== null;

  return (
    <Card variant="elevated" data-testid="ticket-metrics">
      <CardHeader>
        <h3 className="text-h5 text-content-primary">Soporte</h3>
        <p className="text-caption text-content-tertiary">
          El bot atiende primero. Lo que no puede resolver queda como ticket, con la conversación
          que lo originó.
        </p>
      </CardHeader>

      <CardBody className="flex flex-col gap-6">
        {!hayAlgo ? (
          <EmptyState message="Todavía no hay tickets en este período. Aparecen acá cuando el bot registra un problema que no pudo resolver en la conversación." />
        ) : (
          <>
            <div className="grid grid-cols-2 gap-6 lg:grid-cols-4">
              {/* Cero abiertos es un dato bueno: esta tarjeta se dibuja siempre. */}
              <Dato label="Tickets abiertos" valor={String(backlog.abiertos)} />
              <Dato
                label="Cerrados"
                valor={String(backlog.cerrados)}
                hint="En el período seleccionado"
              />

              {/*
                `null` = no hay cerrados de los que sepamos si intervino alguien.
                El `hint` dice el denominador a propósito: "71%" a secas esconde
                que se calculó sobre 7 y no sobre todos los cerrados.
              */}
              {resueltoPorBot ? (
                <Dato
                  label="Resueltos por el bot"
                  valor={`${Math.round(resueltoPorBot.rate * 100)}%`}
                  hint={`${resueltoPorBot.porElBot} de ${resueltoPorBot.conDato} · sin que nadie del equipo interviniera`}
                />
              ) : null}

              {/*
                "Del equipo" no es un adorno de la etiqueta. Sin esa palabra el
                cliente la lee como el tiempo total de respuesta, y como el bot
                contesta en segundos la métrica mentiría por presentación.
              */}
              {primeraRespuestaHumana ? (
                <Dato
                  label="Primera respuesta del equipo"
                  valor={duracion(primeraRespuestaHumana.medianaSeg)}
                  hint={`mediana sobre ${primeraRespuestaHumana.escalados} que llegaron a una persona`}
                />
              ) : null}

              {tiempoResolucion ? (
                <Dato
                  label="Tiempo de resolución"
                  valor={duracion(tiempoResolucion.medianaSeg)}
                  hint={`mediana, de apertura a cierre · ${tiempoResolucion.cerrados} tickets`}
                />
              ) : null}

              {tickets.reabiertos > 0 ? (
                <Dato
                  label="Reabiertos"
                  valor={String(tickets.reabiertos)}
                  hint="Volvieron después de cerrarse"
                />
              ) : null}

              {/*
                Los que no dicen si intervino alguien: anteriores a que
                midiéramos esto. Se declaran en vez de esconderse, porque
                explican por qué el denominador de arriba es más chico.
              */}
              {backlog.sinDato > 0 ? (
                <Dato
                  label="Sin dato de intervención"
                  valor={String(backlog.sinDato)}
                  hint="Anteriores a que midiéramos esto · fuera de las tasas"
                />
              ) : null}
            </div>

            {porAgente ? (
              <div className="flex flex-col gap-2">
                <span className="text-caption text-content-tertiary">Por persona</span>
                <div className="overflow-x-auto">
                  <table className="w-full text-body">
                    <thead>
                      <tr className="text-caption text-content-tertiary">
                        <th className="py-1 text-left font-normal">Responsable</th>
                        <th className="py-1 text-right font-normal">Abiertos</th>
                        <th className="py-1 text-right font-normal">Cerrados</th>
                      </tr>
                    </thead>
                    <tbody>
                      {porAgente.map((a) => (
                        <tr key={a.assignedTo} className="border-edge-subtle border-t">
                          <td className="py-1 text-content-primary">{a.assignedTo}</td>
                          <td className="py-1 text-right tabular-nums text-content-primary">
                            {a.abiertos}
                          </td>
                          <td className="py-1 text-right tabular-nums text-content-primary">
                            {a.cerrados}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </>
        )}
      </CardBody>
    </Card>
  );
}
